import styles from "./styles.module.css";
import { openModal, closeModal } from "src/store/slices/modalSlice";
import { useDispatch } from "react-redux";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import closeIcon from "../../../../assets/icons/closeGray.svg";
import { useFormik } from "src/hooks/useFormik";
import * as Yup from "yup";
import { Input } from "src/components/atoms/Input";
import CurcleInfo from "../../../../assets/icons/curcleInfo.svg";
import React, { useState } from "react";
import { Checkbox } from "src/components/atoms/Checkbox";
import { ProtectedImage } from "src/components/atoms/ProtectedImage";
import PlusBlack from "../../../../assets/icons/plusBlack.svg";
import { apiClient } from "src/api/client";
import { FoundClient } from "src/api/types";

const validationSchema = Yup.object({
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),
});

export const AddNewClient: React.FC = () => {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [foundClients, setFoundClients] = useState<FoundClient[]>([]);
  const [notFoundError, setNotFoundError] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState<number[]>([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  const searchClient = async () => {
    if (!formik.values.email) {
      return;
    }

    setLoading(true);
    setFoundClients([]);
    setChecked(false);
    setSelectedAccounts([]);
    setNotFoundError("");

    try {
      const response = await apiClient.findProFileByEmail({
        email: formik.values.email,
      });
      if (response) {
        setFoundClients([response]);
        setChecked(true);
      }
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 400) {
        setNotFoundError("User with this email was not found.");
      } else {
        setNotFoundError("User with this email was not found");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    if (selectedAccounts.length < 1) {
      setNotFoundError("Please select at least one account");
      return;
    }

    setRequestLoading(true);

    try {
      const requests = selectedAccounts.map((clientId) =>
        apiClient.createLink({ client_id: clientId }),
      );
      await Promise.all(requests);

      setRequestSuccess(true);
    } catch (error: any) {
      setNotFoundError("An error occured while accessing");
    } finally {
      setRequestLoading(false);
    }
  };

  const toggleAccount = (id: number) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const openAddClient = () => {
    dispatch(closeModal({ type: "addNewClient" }));
    dispatch(openModal({ type: "addClient" }));
  };

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: async () => {
      await searchClient();
    },
  });

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Add new client</h3>
          <div>
            <Button
              text=""
              icon={closeIcon}
              btnType={BUTTONTYPE.WITHOUT_BORDER}
              onClick={() => dispatch(closeModal({ type: "addNewClient" }))}
            />
          </div>
        </div>
        <div className={styles.modalInfo}>
          <img src={CurcleInfo} alt="" />
          <div>
            Enter the client's email address, we will check if they are already
            registered. If the user is not there, add them yourself, and if
            found, request access.
          </div>
        </div>
        <form onSubmit={formik.handleSubmit} className={styles.form}>
          <Input
            value={formik.values.email}
            label="Email"
            onChange={(e) => {
              formik.handleChange(e);
              setNotFoundError("");
              setChecked(false);
              setFoundClients([]);
            }}
            name="email"
            error={formik.touched.email && formik.errors.email}
          />
          {notFoundError && (
            <p className={styles.notFoundError}>{notFoundError}</p>
          )}
          {checked && foundClients.length > 0 && (
            <div className={styles.modalAcc}>
              <div className={styles.modalAccTitle}>
                Accounts found ({foundClients.length})
              </div>
              <div>
                {foundClients.map((acc) => (
                  <div
                    key={acc.id}
                    className={styles.userItem}
                    style={{
                      backgroundColor: selectedAccounts.includes(acc.id)
                        ? "#F8F9FA"
                        : "transparent",
                    }}
                  >
                    <div className={styles.userItemLeft}>
                      <div className={styles.avatar}>
                        <ProtectedImage src={acc.picture} alt="" />
                      </div>
                      <div className={styles.userText}>
                        <span>{acc.first_name}</span>
                        <div>{acc.email}</div>
                      </div>
                    </div>
                    <Checkbox
                      checked={selectedAccounts.includes(acc.id)}
                      onChange={() => toggleAccount(acc.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {!checked && (
            <Button
              text={loading ? "Searching..." : "Check"}
              onClick={() => {
                formik.handleSubmit();
              }}
              disabled={loading}
            />
          )}

          {checked && (
            <Button
              text="Request access"
              type="submit"
              disabled={requestLoading || selectedAccounts.length === 0}
              onClick={handleRequestAccess}
            />
          )}
          {requestSuccess && <p> Access request sent successfully!</p>}
        </form>
        <Button
          text="Create another account"
          icon={PlusBlack}
          btnType={BUTTONTYPE.BG_WHITE}
          onClick={openAddClient}
        />
      </div>
    </div>
  );
};
